import { useCallback } from 'react';
import classNames from 'classnames';

import { NumberFieldAllowedKeys } from '../helpers/number-field.const';
import { EventType, NumberFieldProps, StepperDirection } from '../number-field.types';

import { useStepper, UseStepperProps } from './use-stepper';

type UseStepperEventsProps = Pick<
  NumberFieldProps,
  'inputSize' | 'onKeyDown' | 'onValueChange'
> & {
  stepperProps: UseStepperProps;
};

/**
 * Custom hook to handle stepper events for numeric input fields.
 * It provides functionality for incrementing and decrementing values
 * using keyboard and mouse events, while respecting formatting options.
 *
 * @param props - The properties for the stepper events, including input reference and formatting options.
 * @returns An object containing event handlers and arrow button properties.
 */
export function useStepperEvents(props: UseStepperEventsProps) {
  const { inputSize, onKeyDown, onValueChange, stepperProps } = props;
  const stepper = useStepper(stepperProps);

  const updateValue = useCallback(
    (direction: StepperDirection) => {
      const input = stepperProps.inputRef.current;
      if (!input) return;

      return stepper()?.updateInputValue(direction);
    },
    [stepperProps.inputRef, stepper]
  );

  /**
   * Provides the incrementing and decrementing
   * functionality for the number input and
   * blocks non-numeric input.
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(e);

      if (e.key === 'Backspace' || e.key === 'Delete') {
        return; // Allow backspace and delete keys
      }

      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const result = updateValue(
          e.key === 'ArrowUp' ? StepperDirection.Up : StepperDirection.Down
        );
        result && onValueChange?.(result.raw, EventType.Stepper);
        return;
      }

      // Block non-numeric input
      if (!/^\d$/.test(e.key) && !NumberFieldAllowedKeys.includes(e.key)) {
        e.preventDefault();
      }
    },
    [onKeyDown, onValueChange, updateValue]
  );

  /**
   * Handles the mouse wheel event to increment or decrement
   * the value based on the scroll direction.
   */
  const handleWheel = useCallback(
    (e: React.WheelEvent<HTMLInputElement>) => {
      const direction = e.deltaY < 0 ? StepperDirection.Up : StepperDirection.Down;
      const result = updateValue(direction);
      result && onValueChange?.(result.raw, EventType.Wheel);
    },
    [onValueChange, updateValue]
  );

  const arrowProps = useCallback(
    (direction: StepperDirection) => ({
      tabIndex: -1,
      'aria-label': `Increase value by step (${direction})`,
      onClick: (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        // This ensures the input is focused
        // and prevent other events from stop working
        // like the wheel event or arrow keys
        stepperProps.inputRef.current?.focus();
        const result = updateValue(direction);
        result && onValueChange?.(result.raw, EventType.Stepper);
      },
      className: classNames(`arrow-${direction}`, inputSize),
    }),
    [inputSize, onValueChange, stepperProps.inputRef, updateValue]
  );

  return { handleKeyDown, handleWheel, arrowProps };
}
